/**
 * features/hr/EditStaffModal.tsx
 * ------------------------------------------------------------------
 * HR DOMAIN — edit a staff member's profile / personal information.
 *
 * Mirrors the "Add staff" form (same field set + glass styling) but
 * pre-filled from an existing record and writing via hr.staff.update.
 * Adds the two fields that only matter once a person exists: their
 * employment Status and Start date. Saving routes through the shared
 * SaveConfirmation overlay, consistent with the rest of HR.
 *
 * Opened from the staff profile header (Edit button), gated to HR.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { Save, Loader2, UserCog } from 'lucide-react';
import { AccentButton, glassFieldClass, FormModal } from '@/components/ui/glass';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { hr } from '@/features/hr/hrWrite';
import { recordHrAudit } from '@/lib/hrAudit';
import { useAuth } from '@/contexts/AuthContext';
import {
  STAFF_CATEGORY_LABELS,
  RESIDENCY_LABELS,
  type HrStaff,
  type StaffCategory,
  type ResidencyStatus,
  type StaffStatus,
} from '@/lib/hr';

const STATUS_LABELS: Record<StaffStatus, string> = {
  active: 'Active',
  on_leave: 'On leave',
  suspended: 'Suspended',
  exited: 'Exited',
};

export default function EditStaffModal({
  staff,
  onClose,
}: {
  staff: HrStaff;
  onClose: () => void;
}) {
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };
  const [firstName, setFirstName] = useState(staff.firstName ?? '');
  const [middleName, setMiddleName] = useState(staff.middleName ?? '');
  const [lastName, setLastName] = useState(staff.lastName ?? '');
  const [employeeNo, setEmployeeNo] = useState(staff.employeeNo ?? '');
  const [category, setCategory] = useState<StaffCategory>(staff.category);
  const [jobTitle, setJobTitle] = useState(staff.jobTitle ?? '');
  const [department, setDepartment] = useState(staff.department ?? '');
  const [email, setEmail] = useState(staff.email ?? '');
  const [phone, setPhone] = useState(staff.phone ?? '');
  const [residency, setResidency] = useState<ResidencyStatus>(staff.residency);
  const [status, setStatus] = useState<StaffStatus>(staff.status);
  const [startDate, setStartDate] = useState((staff.startDate ?? '').slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  const canSave =
    !!firstName.trim() && !!lastName.trim() && !!jobTitle.trim() && !!employeeNo.trim();

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setSaveModal({ show: true, status: 'saving', message: 'Saving profile…' });
    try {
      await hr.staff.update(staff.id, {
        employeeNo: employeeNo.trim(),
        firstName: firstName.trim(),
        middleName: middleName.trim() || undefined,
        lastName: lastName.trim(),
        category,
        jobTitle: jobTitle.trim(),
        department: department.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        residency,
        status,
        startDate: startDate ? new Date(startDate).toISOString() : staff.startDate,
      });
      await recordHrAudit({
        action: 'staff.update',
        actor,
        subjectId: staff.id,
        after: { employeeNo: employeeNo.trim(), name: `${firstName.trim()} ${lastName.trim()}`, category, jobTitle: jobTitle.trim(), status },
      });
      setSaveModal({ show: true, status: 'success', message: 'Profile updated.' });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <FormModal
        onClose={onClose}
        icon={UserCog}
        title="Edit staff profile"
        subtitle={`${staff.firstName} ${staff.lastName}`}
        maxWidth="max-w-lg"
        footer={
          <>
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:hover:bg-gray-700">Cancel</button>
            <AccentButton icon={saving ? Loader2 : Save} disabled={!canSave || saving} onClick={save}>
              {saving ? 'Saving…' : 'Save changes'}
            </AccentButton>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={glassFieldClass('py-2')} />
            </Field>
            <Field label="Middle name (optional)">
              <input value={middleName} onChange={(e) => setMiddleName(e.target.value)} className={glassFieldClass('py-2')} />
            </Field>
            <Field label="Last name">
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={glassFieldClass('py-2')} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Employee number">
              <input value={employeeNo} onChange={(e) => setEmployeeNo(e.target.value)} className={glassFieldClass('py-2')} />
            </Field>
            <Field label="Category">
              <select value={category} onChange={(e) => setCategory(e.target.value as StaffCategory)} className={glassFieldClass('py-2')}>
                {(['teaching', 'management', 'admin', 'support', 'ancillary'] as StaffCategory[]).map((c) => (
                  <option key={c} value={c}>{STAFF_CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Job title">
            <input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="e.g. Mathematics Teacher" className={glassFieldClass('py-2')} />
          </Field>
          <Field label="Department (optional)">
            <input value={department} onChange={(e) => setDepartment(e.target.value)} className={glassFieldClass('py-2')} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Email (for payslips & HR)">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className={glassFieldClass('py-2')}
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Used to email payslips &amp; HR documents. For staff who sign in, their
                login email is managed separately in User Management &rarr; Update Email.
              </p>
            </Field>
            <Field label="Phone (optional)">
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className={glassFieldClass('py-2')} />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Residency">
              <select value={residency} onChange={(e) => setResidency(e.target.value as ResidencyStatus)} className={glassFieldClass('py-2')}>
                {(['citizen', 'work_permit', 'residence', 'pr'] as ResidencyStatus[]).map((r) => (
                  <option key={r} value={r}>{RESIDENCY_LABELS[r]}</option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select value={status} onChange={(e) => setStatus(e.target.value as StaffStatus)} className={glassFieldClass('py-2')}>
                {(['active', 'on_leave', 'suspended', 'exited'] as StaffStatus[]).map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </Field>
            <Field label="Start date">
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={glassFieldClass('py-2')} />
            </Field>
          </div>

          {!canSave && (
            <p className="text-xs text-amber-600 dark:text-amber-300">First name, last name, job title and employee number are required.</p>
          )}
        </div>
      </FormModal>

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => {
          if (saveModal.status === 'success') onClose();
          else setSaveModal((m) => ({ ...m, show: false }));
        }}
      />
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">{label}</span>
      {children}
    </label>
  );
}
